export type AssistantLinkTarget =
  | { kind: 'in-app'; href: string }
  | { kind: 'external'; href: string }
  | { kind: 'none' };

const EXTERNAL_PROTOCOL = /^(?:https?:|mailto:|tel:)/i;

function relativeInstancePath(href: string): string | null {
  try {
    const url = new URL(href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!url.pathname.startsWith('/i/')) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/**
 * Entscheidet, wie ein Markdown-Link aus einer Assistenten-Antwort gerendert wird.
 *
 * Das Modell erfindet gelegentlich Platzhalter (`javascript:void(0)`, `#`, den
 * Feldnamen `project_app_url`). `rehype-sanitize` entfernt davon nur die
 * gefaehrlichen Protokolle, sodass ein `<a>` ohne Ziel uebrig bleibt. Solche
 * Links duerfen nicht wie Links aussehen — sonst klickt der Nutzer ins Leere.
 *
 * Absolute URLs, deren Pfad unter `/i/` liegt, werden als In-App-Link
 * behandelt — der Host kann falsch sein (altes gespeichertes Modell-Output).
 */
export function classifyAssistantLinkHref(href: string | undefined | null): AssistantLinkTarget {
  const value = String(href ?? '').trim();
  if (!value) return { kind: 'none' };
  if (value.startsWith('/i/')) return { kind: 'in-app', href: value };
  const fromAbsolute = relativeInstancePath(value);
  if (fromAbsolute) return { kind: 'in-app', href: fromAbsolute };
  // Absolute App-Pfade wie /api/ai/knowledge/... oeffnen in einem neuen Tab.
  if (value.startsWith('/')) return { kind: 'external', href: value };
  if (EXTERNAL_PROTOCOL.test(value)) return { kind: 'external', href: value };
  return { kind: 'none' };
}
